/**
 * Stage hover state — transient, not part of the document model.
 *
 * # Why a separate store (vs `state.selection.hoverEntityId`)
 *
 * The plan (B17 hover-highlight) initially proposed a `hoverEntityId`
 * slot inside `state.selection`. Hover updates fire at pointermove
 * frequency (≤ 60 Hz). Threading them through the editor reducer would:
 *   1. Push an audit event per pointermove (transcript spam)
 *   2. Re-run every selector subscribing to `state.selection` /
 *      `state` (Inspector, Properties palette, etc.) on every cursor
 *      hop — measurable React re-render storm on dense scenes
 *   3. Persist into snapshots / undo stack — hover is purely UI; the
 *      AutoCAD analogue is a per-frame render-state byte, not a
 *      persisted document field
 *
 * AutoCAD's hover is a transient render-side overlay (PREVIEWEFFECT
 * driven), not part of the document. The closest model match is a
 * dedicated, lightweight zustand slice readable by Scene3DStage and the
 * ToolTipCard component but invisible to the audit log / undo / DXF
 * round-trip pipeline.
 *
 * # State shape
 *
 *  - `entityId` — the currently-hovered entity id, or null when the
 *    cursor is outside the canvas / over empty space / over a filtered
 *    entity (PREVIEWFILTER bit set for its category).
 *  - `cursorClient` — last-seen cursor position in viewport-relative
 *    pixel coords. Used by the ROLLOVERTIPS card for placement; cleared
 *    to null on pointer-leave.
 *  - `spaceId` — the active space at the time of the hover write. The
 *    Scene3DStage hover-effect uses this to ignore stale writes from a
 *    space that's no longer current (e.g. a layout switch mid-hover).
 */

import { create } from "zustand";

export interface StageHoverPoint {
  /** Cursor X in viewport-relative CSS px. */
  readonly x: number;
  /** Cursor Y in viewport-relative CSS px. */
  readonly y: number;
}

export interface StageHoverState {
  entityId: string | null;
  cursorClient: StageHoverPoint | null;
  spaceId: string | null;
  setHover: (
    entityId: string | null,
    cursorClient: StageHoverPoint | null,
    spaceId: string | null,
  ) => void;
  clear: () => void;
}

export const useStageHoverStore = create<StageHoverState>((set) => ({
  entityId: null,
  cursorClient: null,
  spaceId: null,
  setHover: (entityId, cursorClient, spaceId) => {
    set((prev) => {
      // Avoid no-op writes — zustand's shallow-equality bail-out is per
      // selector, but state mutations still kick subscribers; bail
      // explicitly when nothing changed.
      if (
        prev.entityId === entityId &&
        prev.spaceId === spaceId &&
        prev.cursorClient?.x === cursorClient?.x &&
        prev.cursorClient?.y === cursorClient?.y
      ) {
        return prev;
      }
      return { ...prev, entityId, cursorClient, spaceId };
    });
  },
  clear: () => {
    set((prev) => {
      if (prev.entityId === null && prev.cursorClient === null) return prev;
      return { ...prev, entityId: null, cursorClient: null, spaceId: null };
    });
  },
}));

/** Pure getter for non-React callers (SceneContent, hit-test paths). */
export function getStageHoverState(): StageHoverState {
  return useStageHoverStore.getState();
}
